import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { BlockDef } from "./block.interfaces";
import { buttonField, createBlock, textField } from "./blockBuilder";
import { useDefaultColors } from "./useDefaultColors";
import {
    WithButton,
    BottomAligned,
    FloatingAtBottom,
    LeftAligned,
    WithLink,
    WithButtonProps,
    BottomAlignedProps,
    FloatingAtBottomProps,
    LeftAlignedProps,
    WithLinkProps,
} from "@/views/blocks/banner/banner";
import { useFieldConfig } from "./useFieldConfig";

export function useBanners() {
    const { t } = useI18n();
    const { colorDefaults } = useDefaultColors();
    const { translations } = useFieldConfig();

    const withButtonBlock = computed<BlockDef>(() =>
        createBlock<WithButtonProps>({
            typeId: "WithButton",
            type: "banner",
            name: t("page.blocks.banner.withButton"),
            component: WithButton,
        })
            .withField("title", textField(translations.value.title, "Special Announcement"))
            .withField(
                "description",
                textField(
                    translations.value.description,
                    "Check out our latest features and updates!",
                    translations.value.text
                )
            )
            .withField(
                "button",
                buttonField(
                    translations.value.button,
                    {
                        label: "Learn More",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const bottomAlignedBlock = computed<BlockDef>(() =>
        createBlock<BottomAlignedProps>({
            typeId: "BottomAligned",
            type: "banner",
            name: t("page.blocks.banner.bottomAligned"),
            component: BottomAligned,
        })
            .withField("title", textField(translations.value.title, "Special Announcement"))
            .withField(
                "description",
                textField(
                    translations.value.description,
                    "Check out our latest features and updates!",
                    translations.value.text
                )
            )
            .withField(
                "link",
                buttonField(
                    translations.value.button,
                    {
                        label: "Learn More",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const floatingAtBottomBlock = computed<BlockDef>(() =>
        createBlock<FloatingAtBottomProps>({
            typeId: "FloatingAtBottom",
            type: "banner",
            name: t("page.blocks.banner.floatingAtBottom"),
            component: FloatingAtBottom,
        })
            .withField("title", textField(translations.value.title, "Cookies updated"))
            .withField(
                "description",
                textField(
                    translations.value.description,
                    "We've updated our cookie policy to better protect your privacy.",
                    translations.value.text
                )
            )
            .withField(
                "link",
                buttonField(
                    translations.value.button,
                    {
                        label: "Read more",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const leftAlignedBlock = computed<BlockDef>(() =>
        createBlock<LeftAlignedProps>({
            typeId: "LeftAligned",
            type: "banner",
            name: t("page.blocks.banner.leftAligned"),
            component: LeftAligned,
        })
            .withField("title", textField(translations.value.title, "Special Announcement"))
            .withField(
                "description",
                textField(
                    translations.value.description,
                    "Check out our latest features and updates!",
                    translations.value.text
                )
            )
            .withField(
                "link",
                buttonField(
                    translations.value.button,
                    {
                        label: "Learn More",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    const withLinkBlock = computed<BlockDef>(() =>
        createBlock<WithLinkProps>({
            typeId: "WithLink",
            type: "banner",
            name: t("page.blocks.banner.withLink"),
            component: WithLink,
        })
            .withField("title", textField(translations.value.title, "Special Announcement"))
            .withField(
                "description",
                textField(
                    translations.value.description,
                    "Check out our latest features and updates!",
                    translations.value.text
                )
            )
            .withField(
                "link",
                buttonField(
                    translations.value.button,
                    {
                        label: "Learn More",
                        href: "#",
                    },
                    {
                        label: translations.value.label,
                        href: translations.value.href,
                    }
                )
            )
            .withStyleFields([
                colorDefaults.value.background,
                colorDefaults.value.text,
                colorDefaults.value.brand,
            ])
            .build()
    );

    return {
        blocks: computed(() => [
            withButtonBlock.value,
            bottomAlignedBlock.value,
            floatingAtBottomBlock.value,
            leftAlignedBlock.value,
            withLinkBlock.value,
        ]),
    };
}
